import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { selectUserProjectId } from 'core/userContext';
import {
  getExperimentHeaderMetadata,
  getExperimentId,
  getExperimentLoadingState,
  getExperimentTranslations,
  getExperimentWinningCriteria,
} from 'modules/experiments/ducks/selectors';
import { getDefaultMetrics } from 'modules/experiments/utils';
import { ITERABLE_LOCAL_STORAGE_ID } from 'utils/localStorage';

export const useSelectedMetricsColumn = () => {
  const { messageMedium } = useSelector(getExperimentHeaderMetadata);
  const { name: winningCriteriaName } = useSelector(getExperimentWinningCriteria);
  const loading = useSelector(getExperimentLoadingState);
  const expId = useSelector(getExperimentId);
  const projectId = useSelector(selectUserProjectId);
  const translations = useSelector(getExperimentTranslations);
  const defaultMetricColumns = getDefaultMetrics(messageMedium, winningCriteriaName, translations);
  const localStorageKey = `${ITERABLE_LOCAL_STORAGE_ID}-proj-${projectId}-experiment-${expId}-variant-breakdown-column-selection`;

  const getValueFromLocalStorage = () => {
    try {
      // Get from local storage by key
      const item = window.localStorage.getItem(localStorageKey);

      return item ? JSON.parse(item) : defaultMetricColumns;
    } catch (error) {
      // If error also return initialValue
      return defaultMetricColumns;
    }
  };

  // This is the local hook state of all the selected metric columns.
  // Updating this state will update the local storage
  const [selectedMetricsColumn, setSelectedMetricsColumn] =
    useState<string[]>(getValueFromLocalStorage);

  /**
   * Once we are done loading our localStorage key would have the right
   * experiment id set. At this point we update our local hook state
   * with any value available in the local storage. Or we just set the
   * default metric columns. We again set the default metric column coz
   * at initial setup this could have been an empty array without the
   * message medium being available.
   */
  useEffect(() => {
    if (!loading) {
      setSelectedMetricsColumn(getValueFromLocalStorage());
    }
  }, [localStorageKey, loading]);

  /**
   * We update the local storage only after loading has ended.
   * This will make sure that the local storage is updated as the
   * local hook state gets updated.
   */
  useEffect(() => {
    try {
      if (!loading) {
        window.localStorage.setItem(localStorageKey, JSON.stringify(selectedMetricsColumn));
      }
    } catch (error) {
      // fail doesn't matter too much
    }
  }, [selectedMetricsColumn]);

  return {
    defaultMetricColumns,
    selectedMetricsColumn,
    setSelectedMetricsColumn,
  };
};
